import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Package, Lightning, Lightbulb } from '@phosphor-icons/react';
import { groupSelections, computePro75MarginExample } from '../lib/bundleHelpers';
import { Pro75MarginExample } from './Pro75MarginExample';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that previews the Smart Saver planner's grouping, shows leftover
 * items + suggestions, and lets the admin/customer click "Apply" to actually
 * move items from loose into saved bundles.
 */
const SmartSaverPlannerModal = ({ open, customerId, onClose, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.post(`${API}/cart/apply-smart-saver?customerId=${customerId}&dryRun=true`)
      .then((res) => { if (!cancelled) setPlan(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Planner failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, customerId]);

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/cart/apply-smart-saver?customerId=${customerId}&dryRun=false`);
      onApplied?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;
  const savers = plan?.proposedSavers || [];
  const leftovers = plan?.leftoverItems || [];
  const suggestions = plan?.suggestions || [];
  const total = plan?.totalEstimatedSaving || 0;
  const marginExample = computePro75MarginExample(savers.flatMap((s) => s.products || []), total);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="smart-saver-planner-modal" onClick={() => !applying && onClose?.()}>
      <div className="bg-surface border border-border-color rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 p-5 border-b border-border-color sticky top-0 bg-surface">
          <Lightning size={24} weight="duotone" className="text-gold shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-text-primary" data-testid="planner-title">Smart Saver — preview grouping</h3>
            <p className="text-xs text-text-secondary mt-0.5">
              Review the proposed savers below. Click Apply to move them into your Saved Savers,
              or close to leave the cart unchanged.
            </p>
          </div>
          <button onClick={onClose} disabled={applying} aria-label="Close"
                  data-testid="planner-close"
                  className="text-text-secondary hover:text-text-primary disabled:opacity-50">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && <p className="text-sm text-text-secondary text-center py-6">Planning…</p>}
          {error && <p className="text-sm text-danger bg-danger/10 border border-danger/30 px-3 py-2 rounded">{error}</p>}

          {!loading && !error && (
            <>
              {savers.length === 0 && leftovers.length === 0 && (
                <p className="text-sm text-text-secondary text-center py-6" data-testid="planner-empty">
                  Cart is empty — add loose items first, then try again.
                </p>
              )}

              {savers.length > 0 && (
                <section data-testid="planner-savers">
                  <h4 className="text-xs uppercase tracking-wider text-text-secondary mb-2">
                    Proposed Savers ({savers.length}) — total estimated saving ${total.toFixed(2)}
                  </h4>
                  <ul className="space-y-2">
                    {savers.map((s, i) => (
                      <li
                        key={`${s.kind || 'saver'}-${i}-${s.products?.[0]?.id || ''}`}
                        className="p-3 bg-black/40 border border-gold/40 rounded-lg"
                        data-testid={`planner-saver-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-sm font-semibold text-gold flex items-center gap-2">
                            <Package size={14} weight="duotone" /> {s.label}
                          </span>
                          <span className="text-xs text-success">save ${s.estimatedSaving.toFixed(2)}</span>
                        </div>
                        <ul className="text-xs text-text-secondary space-y-0.5 pl-5">
                          {groupSelections(s.products).map((p) => (
                            <li key={`${p.id}-${p.selectedFlavour || ''}`}>
                              {p.qty}× {p.name}{p.selectedFlavour ? ` — ${p.selectedFlavour}` : ''}{p.size ? ` (${p.size})` : ''}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-text-secondary mt-2">
                          Saver discount: <span className="text-gold">{s.discountPct}%</span>
                          {' '}(supporting items only) · Saver Price ${(s.estimatedSaverPrice ?? s.estimatedSubtotal).toFixed(2)}
                        </p>
                        {s.note && (
                          <p className="text-[11px] text-warning mt-1" data-testid={`planner-saver-note-${i}`}>
                            {s.note}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                  <Pro75MarginExample example={marginExample} context="planner" />
                </section>
              )}

              {leftovers.length > 0 && (
                <section data-testid="planner-leftovers">
                  <h4 className="text-xs uppercase tracking-wider text-text-secondary mb-2">
                    Leftover loose items ({leftovers.length})
                  </h4>
                  <ul className="text-xs text-text-secondary space-y-0.5 pl-5">
                    {leftovers.map((it) => (
                      <li key={`leftover-${it.id}-${it.selectedFlavour || ''}`}>
                        {it.qty}× {it.name}{it.selectedFlavour ? ` — ${it.selectedFlavour}` : ''}{it.size ? ` (${it.size})` : ''}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {suggestions.length > 0 && (
                <section className="bg-warning/10 border border-warning/30 rounded-lg p-3" data-testid="planner-suggestions">
                  <h4 className="text-xs uppercase tracking-wider text-warning mb-2 flex items-center gap-1">
                    <Lightbulb size={14} weight="duotone" /> Suggestions to unlock more savings
                  </h4>
                  <ul className="space-y-2">
                    {suggestions.map((sug, i) => (
                      <li
                        key={`sug-${i}-${sug.core?.id || ''}-${sug.label || ''}`}
                        className="text-xs text-text-primary"
                        data-testid={`planner-suggestion-${i}`}
                      >
                        <div>
                          <span className="font-semibold">{sug.label}</span> with{' '}
                          <em>{sug.core.qty > 1 ? `${sug.core.qty}× ` : ''}{sug.core.name}{sug.core.selectedFlavour ? ` (${sug.core.selectedFlavour})` : ''}</em>:
                        </div>
                        <div className="text-text-secondary ml-3">
                          {sug.needs}
                          {typeof sug.estimatedSavingIfCompleted === 'number' && (
                            <span className="text-success"> (potential save ~${sug.estimatedSavingIfCompleted.toFixed(2)})</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border-color sticky bottom-0 bg-surface">
          <button onClick={onClose} disabled={applying} data-testid="planner-cancel"
                  className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50">
            Close
          </button>
          {savers.length > 0 && (
            <button onClick={apply} disabled={applying || loading} data-testid="planner-apply"
                    className="px-4 py-2 text-sm font-bold bg-gold text-black rounded-lg hover:bg-gold-hover disabled:opacity-50">
              {applying ? 'Applying…' : `Apply ${savers.length} Saver${savers.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartSaverPlannerModal;
